/**
 * MetadataTable Component
 *
 * A truly self-contained, reusable table component that:
 * - Fetches its own metadata and data
 * - Manages all internal state (pagination, sorting, filtering, search)
 * - Requires minimal configuration (just endpoint!)
 *
 * Usage is dramatically simplified compared to the original MetadataTable:
 *
 * BEFORE (250+ lines per page):
 * ```tsx
 * const [page, setPage] = useState(1)
 * const [pageSize, setPageSize] = useState(50)
 * const [sortKey, setSortKey] = useState<string | null>(null)
 * const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
 * const [filters, setFilters] = useState<Filter[]>([])
 * const [searchTerm, setSearchTerm] = useState('')
 * // ... extensive data fetching, filter building, etc.
 * ```
 *
 * AFTER (5-10 lines):
 * ```tsx
 * <MetadataTable endpoint="/offerings" />
 * ```
 */

import { useState, useCallback, useMemo, useEffect, type ReactNode } from 'react'
import { Table, Empty, Skeleton, Button, Space, Grid, Input, Segmented, Badge, Tooltip, Switch, message, Typography } from 'antd'
import { SettingOutlined, PlusOutlined, ReloadOutlined, FilterOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons'
import * as Icons from '@ant-design/icons'
import type { TablePaginationConfig } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'

// Internal hooks
import { useTableState } from './hooks/useTableState'
import { useMetadataFetch } from './hooks/useMetadataFetch'
import { useTableData } from './hooks/useTableData'
import { useColumnPreferences } from './hooks/useColumnPreferences'
import { useActionsColumnPreference } from './hooks/useActionsColumnPreference'
import { useDetailDrawerWithNavigation } from './hooks/useDetailDrawer'
import { usePresets } from './hooks/usePresets'
import { useAdvancedFiltersIntegration } from './hooks/useAdvancedFiltersIntegration'
import { useLifecycleActions } from './hooks/useLifecycleActions'
import { useBulkLifecycleActions } from './hooks/useBulkLifecycleActions'
import { useBulkRecordActions } from './hooks/useBulkRecordActions'
import { useDeletedCount } from './hooks/useDeletedCount'
import { useBulkActivationPreview } from './hooks/useBulkActivationPreview'
// Note: useToolbarCollapse hook REMOVED - now using pure CSS container queries
// See MetadataTable.css for responsive collapse behavior

// Sub-components
import { MetadataTableDrawer } from './MetadataTableDrawer'
import { MetadataTableColumnCustomizer } from './MetadataTableColumnCustomizer'
import { MetadataTableCardView } from './MetadataTableCardView'
import { AdvancedFiltersDrawer } from './AdvancedFilters/AdvancedFiltersDrawer'
import { SlotActions, calculateSlotActionsWidth } from './components/SlotActions'
import { AlwaysVisibleActions } from './components/AlwaysVisibleActions'
import { ActionsDropdown } from './components/ActionsDropdown'
import { PresetDropdown } from './components/PresetDropdown'
import { BulkOperationProgress } from './components/BulkOperationProgress'
import { BulkOperationResults } from './components/BulkOperationResults'
import { MetadataRecordDrawer } from './components/MetadataRecordDrawer'
import { ComparisonModal } from './components/comparison'
import { UnifiedActivationModal } from './components/UnifiedActivationModal'

// Utilities
import { metadataService, isFieldEditable, FieldMetadata } from '@/services/metadataService'
import { getDefaultColumnOrder, getRecommendedColumnOrder } from './utils/columnSorting'
import { calculateColumnWidth, shouldEllipsis } from './utils/columnWidth'
import {
  formatCellValue,
  getCellClassName,
  getStatusBadgeColor,
  isStatusField,
  isLifecycleField,
  renderLifecycleStatus,
} from './utils/columnFormatters'
import { isJsonField, isJsonValue } from './utils/jsonFormatters'
import { JsonValueDisplay } from './components/JsonValueDisplay'

// Types
import type { MetadataTableProps } from './types/selfContained.types'
import type { MetadataColumnType } from './types'
import type { Filter } from '@/types/api.types'

import './MetadataTable.css'

const { useBreakpoint } = Grid
const { Search } = Input
const { Text } = Typography

// Stable empty array to prevent infinite re-render loops
// When baseFilters prop is not provided, using `= []` in destructuring creates
// a new empty array on every render, causing useMemo cascades and query key changes
const EMPTY_BASE_FILTERS: Filter[] = []

// Helper to render icons by name from Ant Design icons
function renderIcon(iconName?: string | null) {
  if (!iconName) return null
  const IconComponent = (Icons as any)[iconName]
  return IconComponent ? <IconComponent /> : null
}

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

/**
 * MetadataTable - Fully self-contained metadata-driven table
 *
 * @example Minimal usage
 * ```tsx
 * <MetadataTable endpoint="/offerings" />
 * ```
 *
 * @example With view metadata
 * ```tsx
 * <MetadataTable
 *   endpoint="/audit_logs"
 *   useViewMetadata={true}
 * />
 * ```
 *
 * @example With customizations
 * ```tsx
 * <MetadataTable
 *   endpoint="/customers"
 *   baseFilters={[{ key: 'deletedAt', constraint_type: 'IS_NULL' }]}
 *   actions={(record) => <Button>View</Button>}
 * />
 * ```
 */
export function MetadataTable({
  // Required
  endpoint,

  // View metadata support
  useViewMetadata = false,

  // Filters
  baseFilters = EMPTY_BASE_FILTERS,

  // Customization
  actions,
  columnOverrides,
  onRowClick,
  showDetailDrawer = true,
  emptyMessage = 'No records found',
  rowKey = 'uid',

  // Callbacks
  onRefresh,
  onError,
  onDataLoad,
  onNew,

  // Display options
  showToolbar = true,
  useExternalFilters = false,
  externalSortOrder,

  // Record drawer
  enableRecordDrawer = true,
  onRecordSaved,

  // Lifecycle actions
  showLifecycleActions,
  onLifecycleOpenEditor,

  // Bulk operations
  bulkOperations,
  enableBulkLifecycle,

  // Record comparison
  enableRecordComparison,
  onCompare: onCompareCallback,

  // Escape hatch
  queryKey,
}: MetadataTableProps) {
  // Responsive breakpoints
  const screens = useBreakpoint()
  const isMobile = !screens.md

  // Progressive toolbar collapse is now handled by pure CSS container queries
  // See MetadataTable.css for @container rules that handle responsive collapse
  // This eliminates React re-renders during resize, preventing flicker

  // Button tooltips - always show since CSS may collapse buttons to icon-only
  // Preset tooltips are handled dynamically based on selection state
  const showButtonTooltips = true

  // Extract model name from endpoint for localStorage keys and display
  const model = useMemo(
    () => metadataService.extractModelFromEndpoint(endpoint),
    [endpoint]
  )

  // ========================================
  // INTERNAL STATE (useTableState hook)
  // ========================================
  const {
    page,
    pageSize,
    // Note: sortKey/sortDirection/handleSort removed - now using unified sort via Advanced Filters
    // Column header clicks sync to appliedAdvancedSortFields via handleTableChange
    filters,
    searchTerm,
    setPage,
    setPageSize,
    setSearchTerm,
  } = useTableState()

  // Debounced search term for API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  // ========================================
  // INTERNAL SELECTION STATE (for bulk operations)
  // ========================================
  const [internalSelectedKeys, setInternalSelectedKeys] = useState<string[]>([])

  // Use external selection if provided, otherwise internal
  const selectedRowKeys = bulkOperations?.selectedRowKeys ?? internalSelectedKeys
  const setSelectedRowKeys = bulkOperations?.onSelectionChange ?? setInternalSelectedKeys

  // ========================================
  // METADATA FETCHING (useMetadataFetch hook)
  // ========================================
  const {
    fields,
    availableColumns,
    isLoading: metadataLoading,
    error: _metadataError, // Handled by errorService (universal error handler)
    formSections,
    display,
    baseFilters: metadataBaseFilters,
    defaultSort,
    defaultFilters,
    presets,
    searchConfig,
    emptyMessage: metadataEmptyMessage,
    lifecycle,
    api,
    recordActions,
    comparison,
  } = useMetadataFetch({ endpoint, useViewMetadata })

  // ========================================
  // TRASH VIEW TOGGLE (for viewing deleted records)
  // ========================================
  const [showDeleted, setShowDeleted] = useState(false)
  // Store preset before entering trash view (to restore when exiting)
  const [preTrashPreset, setPreTrashPreset] = useState<string | null>(null)

  // Determine if record actions are enabled (has delete/undelete/purge capabilities)
  const recordActionsEnabled = Boolean(recordActions && Object.keys(recordActions).length > 0)

  // Check if undelete is available (needed to show trash toggle)
  const hasUndeleteAction = Boolean(recordActions?.undelete)

  // Fetch count of deleted records (only if undelete is available)
  const {
    count: deletedCount,
    refetch: refetchDeletedCount,
  } = useDeletedCount({
    endpoint,
    enabled: hasUndeleteAction && !metadataLoading,
    baseFilters: baseFilters,
  })

  // ========================================
  // ADVANCED FILTERS INTEGRATION
  // ========================================
  const {
    apiFilters: advancedApiFilters,
    activeFilterCount: advancedFilterCount,
    appliedSortFields: appliedAdvancedSortFields,
    setAppliedSortFieldsDirectly,
    toolbarProps: advancedFiltersToolbarProps,
    drawerProps: advancedFiltersDrawerProps,
  } = useAdvancedFiltersIntegration({
    model,
    defaultFilters,
    defaultSort,
    fields,
    onFiltersChange: () => {
      setPage(0)
      // Clear selection when filters change (rows may not exist in new result set)
      setSelectedRowKeys([])
    },
    onSortsChange: () => setPage(0), // Don't clear selection on sort (same rows, different order)
    baseFilters,
    hasLifecycle: !!lifecycle,
  })

  // Convert applied sort fields to order_by format for API
  // Uses appliedAdvancedSortFields (not draft state) so table only updates on Apply
  // Note: appliedAdvancedSortFields is initialized with defaultSort from useSorting hook,
  // so this will always have a value (at minimum, the metadata defaults)
  const advancedOrderBy = useMemo(() => {
    return appliedAdvancedSortFields.map((s) => ({ key: s.key, direction: s.direction }))
  }, [appliedAdvancedSortFields])

  // ========================================
  // PRESETS (usePresets hook) - after Advanced Filters for coexistence
  // ========================================
  const {
    selectedPreset,
    handlePresetChange,
    presetFilters,
  } = usePresets({
    presets,
    advancedFiltersActive: advancedFilterCount > 0,
    onClearAdvancedFilters: advancedFiltersDrawerProps.onResetFiltersToDefaults,
    onPageReset: () => {
      setPage(0)
      // Clear selection when filters change (rows may not exist in new result set)
      setSelectedRowKeys([])
    },
  })

  // Presets that are incompatible with trash view (only drafts can be deleted)
  const TRASH_INCOMPATIBLE_PRESETS = ['active', 'expired']

  // Handler for trash toggle with preset coordination
  const handleTrashToggle = useCallback((showTrash: boolean) => {
    if (showTrash) {
      // Entering trash view
      // Store current preset to restore later
      setPreTrashPreset(selectedPreset)

      // If current preset is incompatible with trash view, auto-switch to 'all'
      if (selectedPreset && TRASH_INCOMPATIBLE_PRESETS.includes(selectedPreset)) {
        handlePresetChange('all')
        message.info('Switched to "All States" - only draft records can be deleted')
      }
    } else {
      // Exiting trash view - restore previous preset if we stored one
      if (preTrashPreset) {
        handlePresetChange(preTrashPreset)
        setPreTrashPreset(null)
      }
    }

    setShowDeleted(showTrash)
    setPage(0)
    setSelectedRowKeys([])
  }, [selectedPreset, preTrashPreset, handlePresetChange, setPage, setSelectedRowKeys])

  // Merge base filters: metadata first, then props layer on top
  // Cast DefaultFilter[] to Filter[] since they are structurally compatible
  // When showDeleted is true, replace any deletedAt filter with IS_NOT_NULL to show only deleted records
  const effectiveBaseFilters = useMemo(() => {
    // Start with metadata base filters
    const filters = [...(metadataBaseFilters as Filter[])]

    // Add prop-based filters, but filter out any deletedAt filter if showDeleted mode is active
    // (we'll add our own deletedAt filter based on the toggle)
    const propFilters = showDeleted
      ? baseFilters.filter(f => f.key !== 'deletedAt')
      : baseFilters

    filters.push(...propFilters)

    // Add the appropriate deletedAt filter based on toggle
    if (showDeleted) {
      // Trash view: show only deleted records
      filters.push({ key: 'deletedAt', constraint_type: 'IS_NOT_NULL' })
    } else {
      // Normal view: ensure deleted records are excluded (unless already filtered)
      const hasDeletedFilter = filters.some(f => f.key === 'deletedAt')
      if (!hasDeletedFilter) {
        // Add soft delete exclusion if not already present
        filters.push({ key: 'deletedAt', constraint_type: 'IS_NULL' })
      }
    }

    return filters
  }, [metadataBaseFilters, baseFilters, showDeleted])

  // Empty message priority: prop > metadata > default
  const effectiveEmptyMessage = emptyMessage ?? metadataEmptyMessage ?? 'No records found'

  // Combine all filters: base + presets + advanced + user filters
  // When useExternalFilters is true, skip internal filters (parent controls via baseFilters)
  const combinedFilters = useMemo(() => {
    if (useExternalFilters) {
      // External mode: only use user-applied filters (parent handles presets/search via baseFilters)
      return [...filters]
    }
    // Internal mode: combine all filter sources
    return [...presetFilters, ...advancedApiFilters, ...filters]
  }, [useExternalFilters, presetFilters, advancedApiFilters, filters])

  // ========================================
  // DATA FETCHING (useTableData hook)
  // ========================================
  const {
    data,
    total,
    returned: _returned, // Available for future pagination enhancements
    hasMore: _hasMore, // Available for infinite scroll implementation
    serverOffset: _serverOffset, // Server-confirmed offset
    serverLimit: _serverLimit, // Server-confirmed limit
    isLoading: dataLoading,
    isFetching,
    error: _dataError, // Handled by errorService (universal error handler)
    refetch,
  } = useTableData({
    endpoint,
    page,
    pageSize,
    // Note: sortKey/sortDirection removed - now using unified externalOrderBy from Advanced Filters
    sortKey: null,
    sortDirection: null,
    filters: combinedFilters,
    searchTerm: useExternalFilters ? '' : debouncedSearchTerm,
    baseFilters: effectiveBaseFilters,
    fields,
    searchConfig,
    defaultSort,
    queryKey,
    enabled: !metadataLoading && Object.keys(fields).length > 0,
    onError,
    // Unified sort: advancedOrderBy now handles all sort state (column clicks + AF drawer)
    // Falls back to externalSortOrder prop if provided
    externalOrderBy: advancedOrderBy.length > 0 ? advancedOrderBy : externalSortOrder,
    // Pass lifecycle for auto-filtering valid states (excludes garbage data)
    lifecycle,
    // Include deleted records when trash view is active
    includeDeleted: showDeleted,
  })

  // Notify parent when data changes (for bulk operations, etc.)
  useEffect(() => {
    if (onDataLoad && data) {
      onDataLoad(data, total)
    }
  }, [data, total, onDataLoad])

  // ========================================
  // LIFECYCLE ACTIONS (useLifecycleActions hook)
  // ========================================
  const {
    lifecycleEnabled,
    handleActionSuccess: handleLifecycleActionSuccess,
  } = useLifecycleActions({
    endpoint,
    queryKey,
    lifecycle,
    onActionSuccess: onRefresh,
  })

  // Determine if lifecycle actions should be shown
  // Default: true if lifecycle is enabled and prop not explicitly set to false
  const shouldShowLifecycleActions = showLifecycleActions ?? lifecycleEnabled

  // ========================================
  // BULK LIFECYCLE ACTIONS (useBulkLifecycleActions hook)
  // ========================================

  // Handler for bulk duplicate - filters a record to only include editable fields
  // Uses the category-based field system to determine writability
  const getDuplicatePayload = useCallback((record: Record<string, unknown>): Record<string, unknown> => {
    // ONLY include fields that are:
    // 1. Present in the metadata (fields object)
    // 2. Marked as editable by isFieldEditable (respects category, effective_writable, system, readonly)
    // Fields not in metadata are EXCLUDED (they're likely computed/system fields)
    return Object.fromEntries(
      Object.entries(record).filter(([key]) => {
        // Must be in metadata
        if (!fields || !fields[key]) {
          return false
        }
        // Must be editable according to category system
        return isFieldEditable(fields[key] as FieldMetadata)
      })
    )
  }, [fields])

  const {
    enabled: bulkLifecycleEnabled,
    bulkActions,
    execute: executeBulkAction,
    cancel: cancelBulkAction,
    progress: bulkProgress,
    results: bulkResults,
    closeResults: closeBulkResults,
  } = useBulkLifecycleActions({
    endpoint,
    lifecycle,
    data,
    selectedRowKeys,
    queryKey,
    onSuccess: onRefresh,
    showDeleted,
    getDuplicatePayload,
  })

  // ========================================
  // BULK RECORD ACTIONS (useBulkRecordActions hook)
  // ========================================
  const {
    enabled: bulkRecordActionsEnabled,
    bulkActions: bulkRecordActionsList,
    execute: executeBulkRecordAction,
    cancel: cancelBulkRecordAction,
    progress: bulkRecordProgress,
    results: bulkRecordResults,
    closeResults: closeBulkRecordResults,
  } = useBulkRecordActions({
    endpoint,
    recordActions,
    lifecycle,
    data,
    selectedRowKeys,
    queryKey,
    onSuccess: onRefresh,
    showDeleted,
  })

  // Determine if bulk record actions are available (for always-visible actions)
  const hasBulkRecordActions = bulkRecordActionsEnabled

  // Combined flag: do we have any bulk actions that should be shown in always-visible toolbar?
  const hasBulkActions = (enableBulkLifecycle ?? bulkLifecycleEnabled) || hasBulkRecordActions

  // Combine all bulk actions for the always-visible toolbar
  const allBulkActions = useMemo(() => {
    const combined = [
      ...bulkActions,
      ...bulkRecordActionsList.map(action => ({
        ...action,
        key: `record_${action.key}` as string,
      })),
    ]
    return combined
  }, [bulkActions, bulkRecordActionsList])

  // ========================================
  // UNIFIED BULK ACTIVATION PREVIEW
  // ========================================

  // Compute selected records for bulk activation preview
  const selectedRecords = useMemo(() => {
    return data.filter(record => selectedRowKeys.includes(String(record[rowKey] ?? '')))
  }, [data, selectedRowKeys, rowKey])

  // Get records that are applicable for activation (in draft state)
  const activatableRecords = useMemo(() => {
    if (!lifecycle?.enabled) return selectedRecords
    const stateField = lifecycle.stateField
    const activateAction = lifecycle.actions?.activate
    if (!activateAction) return []
    return selectedRecords.filter(record => {
      const currentState = record[stateField] as string | undefined
      return currentState && activateAction.fromStates.includes(currentState)
    })
  }, [selectedRecords, lifecycle])

  // Bulk activation preview hook
  const {
    fetchPreviews: fetchActivationPreviews,
    isLoading: isLoadingActivationPreview,
    progress: activationPreviewProgress,
    previewData: activationPreviewData,
    toggleRecordSelection: toggleActivationSelection,
    selectAllSupersessions,
    deselectAllSupersessions,
    getActivationUids,
    getActivationCount,
    reset: resetActivationPreview,
  } = useBulkActivationPreview({
    endpoint,
    records: activatableRecords,
  })

  // State for unified activation modal visibility
  const [showUnifiedActivationModal, setShowUnifiedActivationModal] = useState(false)

  // Handler for activation with preview flow
  const handleActivateWithPreview = useCallback(async () => {
    // Fetch previews for all activatable records
    const preview = await fetchActivationPreviews()

    // If no supersessions, show simple confirm then use standard bulk machinery
    // This ensures consistent behavior (progress bar, results modal) across all activations
    if (preview.supersessionRecords.length === 0 && preview.failedRecords.length === 0) {
      const count = preview.readyRecords.length
      const entityLabel = display?.label?.toLowerCase() || 'records'
      const Modal = await import('antd').then(m => m.Modal)
      // Track confirmation state for afterClose
      let confirmed = false
      Modal.confirm({
        title: 'Activate Records',
        content: `Activate ${count} ${entityLabel}? No existing versions will be affected.`,
        okText: 'Activate',
        cancelText: 'Cancel',
        onOk: () => {
          // Just set the flag - don't execute here
          // The modal will close and afterClose will fire
          confirmed = true
        },
        onCancel: () => {
          resetActivationPreview()
        },
        afterClose: () => {
          // This fires AFTER the modal has fully closed (animation complete)
          if (confirmed) {
            // Use double requestAnimationFrame to ensure DOM is fully updated
            // before showing progress modal
            requestAnimationFrame(() => {
              requestAnimationFrame(async () => {
                await executeBulkAction('activate')
                resetActivationPreview()
              })
            })
          }
        },
      })
      return
    }

    // Has supersessions or failures - show unified modal
    setShowUnifiedActivationModal(true)
  }, [fetchActivationPreviews, display?.label, executeBulkAction, resetActivationPreview])

  // Handler for unified activation modal confirm
  const handleUnifiedActivationConfirm = useCallback(async (uids: string[]) => {
    setShowUnifiedActivationModal(false)

    // Execute activation for each selected UID
    // The bulk action executor already handles progress tracking
    for (const uid of uids) {
      // Find the record to get its state
      const record = data.find(r => String(r[rowKey]) === uid)
      if (record) {
        // Call the activate endpoint directly
        const { apiService } = await import('@/services/apiService')
        await apiService.post(`${endpoint}/${uid}/activate`, { createContinuations: true })
      }
    }

    // Invalidate queries to refresh data
    // Note: Can't use hooks in callbacks, so we'll rely on onRefresh
    onRefresh?.()
    resetActivationPreview()
    setSelectedRowKeys([])
  }, [data, rowKey, endpoint, onRefresh, resetActivationPreview, setSelectedRowKeys])

  // Handler for unified activation modal cancel
  const handleUnifiedActivationCancel = useCallback(() => {
    setShowUnifiedActivationModal(false)
    resetActivationPreview()
  }, [resetActivationPreview])

  // ========================================
  // COLUMN PREFERENCES (reuse existing hook)
  // ========================================
  const defaultColumns = useMemo(
    () => getDefaultColumnOrder(fields, formSections),
    [fields, formSections]
  )

  const recommendedColumns = useMemo(
    () => getRecommendedColumnOrder(fields, formSections),
    [fields, formSections]
  )

  const {
    visibleColumns,
    preset,
    setVisibleColumns,
  } = useColumnPreferences(model, defaultColumns, availableColumns, recommendedColumns)

  // ========================================
  // ACTIONS COLUMN PREFERENCE (global, applies to all tables)
  // ========================================
  const {
    actionsVisible,
    setActionsVisible,
  } = useActionsColumnPreference()

  // ========================================
  // DETAIL DRAWER (legacy - used when enableRecordDrawer is false)
  // ========================================
  const {
    selectedRecord,
    isOpen: drawerOpen,
    openDrawer,
    closeDrawer,
  } = useDetailDrawerWithNavigation(data, rowKey)

  // ========================================
  // RECORD DRAWER (new unified create/edit/view drawer)
  // ========================================
  const [recordDrawerState, setRecordDrawerState] = useState<{
    open: boolean
    mode: 'create' | 'edit' | 'view'
    record?: Record<string, unknown>
    duplicatedFromRecord?: Record<string, unknown>
    initialValues?: Record<string, unknown>
  }>({
    open: false,
    mode: 'view',
    record: undefined,
    duplicatedFromRecord: undefined,
    initialValues: undefined,
  })

  // Determine if current record is editable based on lifecycle state
  const isRecordEditable = useCallback((record: Record<string, unknown>): boolean => {
    if (!lifecycle?.enabled) return true // No lifecycle = always editable
    const currentState = record[lifecycle.stateField] as string
    const editableStates = ['draft'] // Default editable states
    return editableStates.includes(currentState)
  }, [lifecycle])

  // Open record drawer in appropriate mode
  const openRecordDrawer = useCallback((
    record: Record<string, unknown> | undefined,
    mode: 'create' | 'edit' | 'view',
    options?: {
      duplicatedFromRecord?: Record<string, unknown>
      initialValues?: Record<string, unknown>
    }
  ) => {
    setRecordDrawerState({
      open: true,
      mode,
      record,
      duplicatedFromRecord: options?.duplicatedFromRecord,
      initialValues: options?.initialValues,
    })
  }, [])

  // Close record drawer
  const closeRecordDrawer = useCallback(() => {
    setRecordDrawerState({
      open: false,
      mode: 'view',
      record: undefined,
      duplicatedFromRecord: undefined,
      initialValues: undefined,
    })
  }, [])

  // ========================================
  // COMPARISON MODE STATE
  // ========================================
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false)
  const [comparisonAnchorRecord, setComparisonAnchorRecord] = useState<Record<string, unknown> | null>(null)

  // Determine if comparison is enabled
  // Default: true if comparison config exists in metadata and not explicitly disabled
  const comparisonEnabled = (enableRecordComparison ?? true) && comparison?.enabled

  // Handle Compare button click from drawer
  const handleCompare = useCallback((record: Record<string, unknown>) => {
    // If external callback provided, call it
    if (onCompareCallback) {
      onCompareCallback(record)
      return
    }

    // Otherwise, open comparison modal
    setComparisonAnchorRecord(record)
    setComparisonModalOpen(true)
  }, [onCompareCallback])

  // Close comparison modal
  const handleCloseComparison = useCallback(() => {
    setComparisonModalOpen(false)
    setComparisonAnchorRecord(null)
  }, [])

  // Handle record saved (from drawer)
  const handleRecordSaved = useCallback((savedRecord: Record<string, unknown>) => {
    closeRecordDrawer()
    onRecordSaved?.(savedRecord)
  }, [closeRecordDrawer, onRecordSaved])

  // Handle lifecycle action that opens editor (e.g., duplicate)
  const handleLifecycleOpenEditor = useCallback((
    record: Record<string, unknown>,
    actionKey: string
  ) => {
    // If parent provided a handler, defer to them
    if (onLifecycleOpenEditor) {
      onLifecycleOpenEditor(record, actionKey)
      return
    }

    // Otherwise, if record drawer is enabled, handle internally
    if (enableRecordDrawer && actionKey === 'duplicate') {
      // Duplicate = Create a new record pre-filled with data from the source record
      // We strip non-writable fields (system/lifecycle/computed/identity) and open in CREATE mode (not edit!)
      // This way, canceling just closes the drawer - nothing to delete
      // Uses getDuplicatePayload which respects the category-based field system
      const initialValues = getDuplicatePayload(record)

      // Open in CREATE mode with pre-filled values
      openRecordDrawer(undefined, 'create', {
        duplicatedFromRecord: record,
        initialValues,
      })
    }
  }, [onLifecycleOpenEditor, enableRecordDrawer, openRecordDrawer, getDuplicatePayload])

  // ========================================
  // COLUMN CUSTOMIZER
  // ========================================
  const [customizerOpen, setCustomizerOpen] = useState(false)

  // ========================================
  // INTERNAL SEARCH INPUT STATE
  // ========================================
  const [searchInput, setSearchInput] = useState('')

  // Update search term when input changes (debounced via debouncedSearchTerm)
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value)
    setSearchTerm(value)
    // Clear selection when search changes (rows may not exist in new result set)
    setSelectedRowKeys([])
  }, [setSearchTerm, setSelectedRowKeys])

  // ========================================
  // COLUMN TITLE WITH DESCRIPTION TOOLTIP
  // ========================================
  const renderColumnTitle = useCallback((
    title: ReactNode,
    description: string | null | undefined
  ): ReactNode => {
    if (!description) return title

    return (
      <Tooltip title={description} trigger={['hover', 'focus']}>
        <span
          tabIndex={0}
          className="column-title-with-description"
          role="button"
          aria-label={typeof title === 'string' ? `${title}. ${description}` : undefined}
        >
          {title}
        </span>
      </Tooltip>
    )
  }, [])

  // ========================================
  // TABLE COLUMNS GENERATION
  // ========================================
  const tableColumns = useMemo<MetadataColumnType[]>(() => {
    if (!fields || Object.keys(fields).length === 0) {
      return []
    }

    // Get columns to render (visible columns or defaults)
    const columnsToRender = visibleColumns.length > 0 ? visibleColumns : defaultColumns

    // Build column configs
    const columns = columnsToRender
      .filter((key) => fields[key])
      .map((fieldKey) => {
        const metadata = fields[fieldKey]
        const override = columnOverrides?.[fieldKey]

        // Determine sort order for this column from unified appliedAdvancedSortFields
        const columnSort = appliedAdvancedSortFields.find(s => s.key === fieldKey)
        const columnSortOrder = columnSort
          ? (columnSort.direction === 'asc' ? 'ascend' : 'descend')
          : undefined

        const resolvedTitle = override?.title ?? metadata.title ?? fieldKey

        const column: MetadataColumnType = {
          fieldKey,
          metadata,
          key: fieldKey,
          dataIndex: fieldKey,
          title: renderColumnTitle(resolvedTitle, metadata?.description),
          width: override?.width ?? calculateColumnWidth(fieldKey, metadata),
          ellipsis: shouldEllipsis(metadata),
          sorter: override?.sortable !== false,
          sortOrder: columnSortOrder,
          showSorterTooltip: false,
          // Custom sort icons - show blue icons for AF-sorted columns with priority numbers
          sortIcon: ({ sortOrder }) => {
            const externalSortActive = appliedAdvancedSortFields.length > 0

            // When Advanced Filters sort is active
            if (externalSortActive) {
              // Check if THIS column is in AF sorts
              const afSortIndex = appliedAdvancedSortFields.findIndex(s => s.key === fieldKey)

              if (afSortIndex !== -1) {
                // This column IS sorted by AF - show blue arrow with priority
                const direction = appliedAdvancedSortFields[afSortIndex].direction
                const priority = afSortIndex + 1 // 1-indexed for display
                const showPriority = appliedAdvancedSortFields.length > 1

                return (
                  <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    {direction === 'asc' ?
                      <Icons.CaretUpFilled style={{ fontSize: 14, color: 'var(--rp-blue-primary)' }} /> :
                      <Icons.CaretDownFilled style={{ fontSize: 14, color: 'var(--rp-blue-primary)' }} />
                    }
                    {showPriority && (
                      <sup style={{ fontSize: 9, color: 'var(--rp-gray-500)', marginLeft: 1 }}>{priority}</sup>
                    )}
                  </span>
                )
              }

              // Other columns NOT sorted by AF - show grey disabled icon
              return <Icons.SwapOutlined style={{ fontSize: 12, color: 'var(--rp-border-subtle)', opacity: 0.5 }} className="sort-icon-disabled" />
            }

            // No external sort - show column header sort state
            if (sortOrder === 'ascend') {
              return <Icons.CaretUpFilled style={{ fontSize: 14, color: 'var(--rp-blue-primary)' }} />
            }
            if (sortOrder === 'descend') {
              return <Icons.CaretDownFilled style={{ fontSize: 14, color: 'var(--rp-blue-primary)' }} />
            }
            return <Icons.SwapOutlined style={{ fontSize: 12, color: 'var(--rp-gray-500)', opacity: 0 }} className="sort-icon-inactive" />
          },
          render: (value: unknown, record: Record<string, unknown>, index: number) => {
            // Custom render
            if (override?.render) {
              return override.render(value, record, index)
            }

            // Lifecycle status
            if (isLifecycleField(fieldKey, metadata) && typeof value === 'string') {
              return renderLifecycleStatus(value)
            }

            // JSON fields - use interactive popover display
            if (isJsonField(metadata) || isJsonValue(value)) {
              return <JsonValueDisplay value={value} />
            }

            // Other status fields
            if (isStatusField(fieldKey, metadata) && typeof value === 'string') {
              return (
                <span style={{ color: getStatusBadgeColor(value) }}>
                  {value}
                </span>
              )
            }

            // Default formatting
            const formattedValue = formatCellValue(value, metadata)
            const cellClass = getCellClassName(value, metadata)

            return cellClass ? (
              <span className={cellClass}>{formattedValue}</span>
            ) : (
              formattedValue
            )
          },
        }

        return column
      })

    // Add actions column if:
    // 1. actionsVisible is true (global user preference)
    // 2. AND there are any actions to show (custom, lifecycle, or record actions)
    const hasCustomActions = Boolean(actions)
    const hasLifecycleActions = shouldShowLifecycleActions && lifecycle
    const hasRecordActions = recordActionsEnabled && recordActions
    const hasAnyActions = hasCustomActions || hasLifecycleActions || hasRecordActions

    if (actionsVisible && hasAnyActions) {
      // Calculate width using SlotActions helper (handles both lifecycle and record actions)
      const slotActionsWidth = calculateSlotActionsWidth(
        hasLifecycleActions ? lifecycle : undefined,
        hasRecordActions ? recordActions : undefined
      )

      // Custom actions width estimate (user-provided render function)
      const customActionsWidth = hasCustomActions ? 80 : 0

      const actionsWidth = Math.max(
        slotActionsWidth + customActionsWidth,
        80  // Minimum width
      )

      columns.push({
        fieldKey: 'actions',
        metadata: {} as any,
        key: 'actions',
        title: 'Actions',
        width: actionsWidth,
        fixed: 'right' as const,
        render: (_: unknown, record: Record<string, unknown>) => {
          const customContent = hasCustomActions && actions ? actions(record) : null

          // Use unified SlotActions for both lifecycle and record actions
          const slotActionsContent = (hasLifecycleActions || hasRecordActions) ? (
            <SlotActions
              lifecycle={hasLifecycleActions ? lifecycle : undefined}
              recordActions={hasRecordActions ? recordActions : undefined}
              fieldMetadata={fields}
              comparisonKeyFields={comparison?.keyFields}
              timelineLabelField={comparison?.timelineLabelField}
              showNewPrefix={comparison?.showNewPrefix}
              titleFields={display?.title_fields}
              record={record}
              endpoint={endpoint}
              entityLabel={display?.label_singular || 'Record'}
              onSuccess={handleLifecycleActionSuccess}
              onOpenEditor={handleLifecycleOpenEditor}
            />
          ) : null

          // If both slot actions and custom content, wrap in Space
          if (slotActionsContent && customContent) {
            return (
              <Space size="small" wrap={false}>
                {slotActionsContent}
                {customContent}
              </Space>
            )
          }

          // Return whichever is available
          return slotActionsContent || customContent || null
        },
      } as MetadataColumnType)
    }

    return columns
  }, [fields, visibleColumns, defaultColumns, columnOverrides, actions, appliedAdvancedSortFields, shouldShowLifecycleActions, lifecycle, endpoint, display, handleLifecycleActionSuccess, handleLifecycleOpenEditor, recordActionsEnabled, recordActions, comparison, actionsVisible])

  // ========================================
  // HANDLERS
  // ========================================
  const handleRowClick = useCallback(
    (record: Record<string, unknown>) => {
      // Custom handler takes precedence
      if (onRowClick) {
        onRowClick(record)
        return
      }

      // New unified record drawer (when enabled)
      if (enableRecordDrawer) {
        const canEdit = isRecordEditable(record)
        openRecordDrawer(record, canEdit ? 'edit' : 'view')
        return
      }

      // Legacy detail drawer (view-only)
      if (showDetailDrawer) {
        openDrawer(record)
      }
    },
    [onRowClick, enableRecordDrawer, isRecordEditable, openRecordDrawer, showDetailDrawer, openDrawer]
  )

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: unknown,
      sorter: SorterResult<Record<string, unknown>> | SorterResult<Record<string, unknown>>[]
    ) => {
      // Handle pagination
      if (pagination.current !== undefined && pagination.pageSize !== undefined) {
        // Convert from 1-indexed (Ant Design) to 0-indexed (internal)
        setPage(pagination.current - 1)
        if (pagination.pageSize !== pageSize) {
          setPageSize(pagination.pageSize)
        }
      }

      // Handle sorting - unified with Advanced Filters sort state
      // Strategy: Compare incoming sorter with our current applied sort state.
      // This is more robust than relying on Ant Design's internal sorter object structure.
      const sorterInfo = Array.isArray(sorter) ? sorter[0] : sorter
      const newSortKey = sorterInfo?.columnKey ?? sorterInfo?.field
      const newOrder = sorterInfo?.order  // 'ascend', 'descend', or undefined (cleared)

      // Get current sort state (what columns show as sorted)
      const currentSort = appliedAdvancedSortFields.length > 0
        ? appliedAdvancedSortFields[0]
        : null

      if (newSortKey && newOrder) {
        // Case 1: User clicked to sort a column (has both key and order)
        const direction = newOrder === 'ascend' ? 'asc' : 'desc'
        // Only update if actually different from current sort
        if (!currentSort || currentSort.key !== String(newSortKey) || currentSort.direction !== direction) {
          setAppliedSortFieldsDirectly([{ key: String(newSortKey), direction }])
          setPage(0)
        }
      } else if (!newSortKey && !newOrder && currentSort) {
        // Case 2: Sorter is empty BUT we currently have an active column sort
        // This means user clicked 3rd time to clear (not just pagination change)
        // Note: If currentSort came from defaults and equals defaults, user is clearing column sort
        // Revert to metadata defaults (or empty if no defaults)
        if (defaultSort && defaultSort.length > 0) {
          setAppliedSortFieldsDirectly(defaultSort.map(s => ({ key: s.key, direction: s.direction })))
        } else {
          setAppliedSortFieldsDirectly([])
        }
        setPage(0)
      }
      // Case 3: Sorter is empty and we have no column sort = pagination only, do nothing
    },
    [setPage, pageSize, setPageSize, setAppliedSortFieldsDirectly, defaultSort, appliedAdvancedSortFields]
  )

  const handleRefresh = useCallback(() => {
    refetch()
    refetchDeletedCount() // Also refresh deleted count
    onRefresh?.()
  }, [refetch, refetchDeletedCount, onRefresh])

  // Handle New button click
  const handleNewClick = useCallback(() => {
    // Custom handler takes precedence
    if (onNew) {
      onNew()
      return
    }

    // Open record drawer in create mode
    if (enableRecordDrawer) {
      openRecordDrawer(undefined, 'create')
    }
  }, [onNew, enableRecordDrawer, openRecordDrawer])

  // Column info for customizer
  const columnInfo = useMemo(
    () =>
      availableColumns.map((key) => ({
        key,
        title: fields[key]?.title ?? key,
        formSection: fields[key]?.form_section ?? undefined,
        tableDefault: fields[key]?.table_default ?? false,
        recommended: fields[key]?.recommended ?? false,
      })),
    [availableColumns, fields]
  )

  // Handler for executing bulk actions (both lifecycle and record)
  const handleBulkExecute = useCallback(async (actionKey: string) => {
    if (actionKey.startsWith('record_')) {
      const recordActionKey = actionKey.replace('record_', '') as 'delete' | 'undelete' | 'purge'
      await executeBulkRecordAction(recordActionKey)
      return
    }

    // Check if this is an opensEditor action (like duplicate)
    const action = bulkActions.find(a => a.key === actionKey)
    if (action?.opensEditor) {
      // Single selection: open drawer with record data (same as row-level action)
      if (selectedRowKeys.length === 1) {
        const record = data.find(r => String(r.uid) === selectedRowKeys[0])
        if (record) {
          handleLifecycleOpenEditor(record, actionKey)
        }
        return
      }

      // Multiple selection: bulk duplicate - create draft copies
      // This is handled by executeBulkAction which will call the bulk duplicate handler
      await executeBulkAction(actionKey)
      return
    }

    // Standard lifecycle action
    await executeBulkAction(actionKey)
  }, [executeBulkAction, executeBulkRecordAction, bulkActions, selectedRowKeys, data, handleLifecycleOpenEditor])

  // ========================================
  // RENDER
  // ========================================

  // NOTE: Error display is handled by errorService (universal error handler)
  // which shows a persistent modal. No inline Alert needed here - that would
  // create duplicate error display. See errorService.tsx for details.

  // Loading state
  if (metadataLoading) {
    return <Skeleton active paragraph={{ rows: 10 }} />
  }

  // Shared toolbar component for both mobile and desktop
  // UNIFIED LAYOUT: All actions always visible (ghost when no selection)
  const renderToolbar = () => {
    if (!showToolbar) return null

    const isExecuting = bulkProgress.isRunning || bulkRecordProgress.isRunning

    // Build toolbar classes - add has-selection for responsive collapse optimization
    const toolbarClasses = [
      'metadata-table-toolbar',
      selectedRowKeys.length > 0 ? 'has-selection' : '',
    ].filter(Boolean).join(' ')

    return (
      <div className={toolbarClasses}>
        {/* LEFT SIDE: Configure/Discover tools */}
        <div className="toolbar-left">
          {/* Presets (metadata-driven quick filters)
              Pattern: "Selected shows label" - selected preset shows icon + truncated label,
              unselected presets show icon only with tooltip for full label.
              CSS handles the label hiding, we just add tooltips for unselected items. */}
          {presets && presets.options && presets.options.length > 0 && (
            <Segmented
              options={presets.options.map(preset => {
                // Disable incompatible presets when in trash view (only drafts can be deleted)
                const isDisabledInTrash = showDeleted && TRASH_INCOMPATIBLE_PRESETS.includes(preset.key)
                const isSelected = selectedPreset === preset.key

                // Tooltip logic:
                // - Disabled in trash: always show explanation tooltip
                // - Selected preset: no tooltip needed (label is visible)
                // - Unselected preset: show label in tooltip (since CSS hides it)
                const tooltipText = isDisabledInTrash
                  ? 'Only draft records can be deleted'
                  : (!isSelected ? preset.label : '')

                const content = (
                  <span
                    className="preset-option"
                    style={isDisabledInTrash ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  >
                    {renderIcon(preset.icon)}
                    <span className="preset-label-text">{preset.label}</span>
                  </span>
                )

                return {
                  label: tooltipText ? (
                    <Tooltip title={tooltipText}>
                      {content}
                    </Tooltip>
                  ) : content,
                  value: preset.key,
                  disabled: isDisabledInTrash,
                }
              })}
              value={selectedPreset}
              onChange={(value) => handlePresetChange(value as string)}
              className="toolbar-presets"
            />
          )}

          {/* PresetDropdown - shown at narrow widths via CSS container query */}
          {presets && presets.options && presets.options.length > 0 && (
            <PresetDropdown
              presets={presets.options}
              selectedPreset={selectedPreset}
              onPresetChange={handlePresetChange}
              showDeleted={showDeleted}
              trashIncompatiblePresets={TRASH_INCOMPATIBLE_PRESETS}
            />
          )}

          {/* Search Input */}
          {searchConfig !== false && (
            <Search
              placeholder={
                searchConfig?.placeholder ??
                (searchConfig?.fields?.length
                  ? `Search by ${searchConfig.fields.join(', ')}`
                  : 'Search...')
              }
              allowClear
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              onSearch={handleSearchChange}
              className="search-input"
            />
          )}

          {/* Advanced Filters Button */}
          <span className="advanced-filters-wrapper">
            <Tooltip title={
              advancedFilterCount > 0
                ? `${advancedFilterCount} filter(s) active`
                : (showButtonTooltips ? 'Advanced Filters & Sort' : '')
            }>
              <Badge count={advancedFilterCount} offset={[-8, 4]}>
                <Button
                  icon={<FilterOutlined />}
                  onClick={advancedFiltersToolbarProps.onOpenAdvancedFilters}
                  className={`advanced-filters-btn ${advancedFilterCount > 0 ? 'active-filters' : ''}`}
                >
                  <span className="toolbar-btn-text">Advanced</span>
                </Button>
              </Badge>
            </Tooltip>
          </span>

          {/* Trash View Toggle - icon only (no text, trashcan icon is descriptive) */}
          {hasUndeleteAction && (
            <Tooltip title={showDeleted ? 'Viewing deleted records' : `Show ${deletedCount} deleted records`}>
              <span className="trash-toggle">
                <Switch
                  size="small"
                  checked={showDeleted}
                  onChange={handleTrashToggle}
                />
                <DeleteOutlined style={{ color: showDeleted ? 'var(--rp-temporal-red)' : undefined }} />
              </span>
            </Tooltip>
          )}

          {/* Columns Button - moved to left side */}
          <Tooltip title="Columns">
            <Button
              icon={<SettingOutlined />}
              onClick={() => setCustomizerOpen(true)}
            >
              <span className="toolbar-btn-text">Columns</span>
            </Button>
          </Tooltip>

          {/* Refresh Button - moved to left side */}
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined spin={isFetching} />}
              onClick={handleRefresh}
              disabled={isFetching}
            >
              <span className="toolbar-btn-text">Refresh</span>
            </Button>
          </Tooltip>
        </div>

        {/* RIGHT SIDE: Actions */}
        <div className="toolbar-right">
          {/* Selection indicator - shows when rows selected
              Uses dual-text spans for responsive abbreviation:
              - Full: "47 selected" (default)
              - Short: "47" (at ≤550px, aligned with presets dropdown) */}
          {selectedRowKeys.length > 0 && (
            <span className="selection-indicator">
              <Text strong>
                <span className="selection-text-full">{selectedRowKeys.length} selected</span>
                <span className="selection-text-short">{selectedRowKeys.length}</span>
              </Text>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => setSelectedRowKeys([])}
                className="clear-selection-btn"
              />
            </span>
          )}

          {/* Always-visible bulk actions (if any bulk actions exist) */}
          {hasBulkActions && allBulkActions.length > 0 && (
            <>
              <AlwaysVisibleActions
                selectedCount={selectedRowKeys.length}
                actions={allBulkActions}
                onExecute={handleBulkExecute}
                isExecuting={isExecuting}
                entityLabel={display?.label?.toLowerCase() || 'items'}
                onActivateWithPreview={handleActivateWithPreview}
              />

              {/* Mobile dropdown fallback - hidden by default, shown via CSS */}
              <ActionsDropdown
                selectedCount={selectedRowKeys.length}
                actions={allBulkActions}
                onExecute={handleBulkExecute}
                isExecuting={isExecuting}
                entityLabel={display?.label?.toLowerCase() || 'items'}
                onActivateWithPreview={handleActivateWithPreview}
              />
            </>
          )}

          {/* Separator between actions and New button */}
          {hasBulkActions && allBulkActions.length > 0 && (onNew || enableRecordDrawer) && api?.endpoints?.create !== false && (
            <span className="toolbar-separator" />
          )}

          {/* New Button */}
          {(onNew || enableRecordDrawer) && api?.endpoints?.create !== false && (
            <Tooltip title={`New ${display?.label_singular || 'Record'}`}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleNewClick}
              >
                <span className="toolbar-btn-text new-btn-text">New {display?.label_singular || ''}</span>
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    )
  }

  // Mobile view - uses shared toolbar + card view
  if (isMobile) {
    return (
      <div className="metadata-table-container metadata-table-mobile">
        {renderToolbar()}

        {/* Card view */}
        <MetadataTableCardView
          model={model}
          data={data}
          fields={fields}
          displayColumns={visibleColumns.slice(0, 4)}
          loading={dataLoading}
          onCardClick={handleRowClick}
          emptyMessage={effectiveEmptyMessage}
        />

        {/* Use correct drawer based on enableRecordDrawer */}
        {enableRecordDrawer ? (
          <MetadataRecordDrawer
            open={recordDrawerState.open}
            onClose={closeRecordDrawer}
            endpoint={endpoint}
            mode={recordDrawerState.mode}
            record={recordDrawerState.record}
            fields={fields}
            formSections={formSections}
            display={display}
            lifecycle={lifecycle}
            api={api}
            onSuccess={handleRecordSaved}
            queryKey={queryKey ?? [endpoint]}
            keyField={rowKey}
            comparison={comparisonEnabled ? comparison : undefined}
            onCompare={comparisonEnabled ? handleCompare : undefined}
            internalState={
              recordDrawerState.duplicatedFromRecord
                ? {
                    isDirty: false,
                    duplicatedFromRecord: recordDrawerState.duplicatedFromRecord,
                    initialValues: recordDrawerState.initialValues,
                  }
                : undefined
            }
          />
        ) : showDetailDrawer && (
          <MetadataTableDrawer
            model={model}
            record={selectedRecord}
            fields={fields}
            formSections={formSections}
            open={drawerOpen}
            onClose={closeDrawer}
          />
        )}
      </div>
    )
  }

  // Desktop view - uses shared toolbar + table view
  return (
    <div className="metadata-table-container">
      {renderToolbar()}

      {/* Table */}
      <Table
        className="metadata-table"
        columns={tableColumns}
        dataSource={data}
        rowKey={rowKey}
        loading={dataLoading || isFetching}
        rowSelection={
          // Enable row selection if:
          // 1. External bulkOperations prop is provided, OR
          // 2. Built-in bulk lifecycle is enabled (and not explicitly disabled)
          (bulkOperations || (enableBulkLifecycle !== false && bulkLifecycleEnabled))
            ? {
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys as string[]),
                type: 'checkbox',
                fixed: 'left',
              }
            : undefined
        }
        pagination={{
          current: page + 1, // Convert 0-indexed to 1-indexed
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['25', '50', '100', '200'],
          showTotal: (total, range) => {
            // Enhanced pagination display with server metadata
            const rangeStart = range[0]
            const rangeEnd = range[1]
            const formattedTotal = total.toLocaleString()

            // Single page - show simplified message
            if (total <= pageSize && rangeStart === 1) {
              return `Showing all ${formattedTotal} records`
            }

            // Normal pagination display
            return `${rangeStart}-${rangeEnd} of ${formattedTotal} records`
          },
        }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: {
            cursor: showDetailDrawer || onRowClick || enableRecordDrawer ? 'pointer' : 'default',
            // Muted styling for deleted records in trash view
            ...(showDeleted && record.deletedAt ? { opacity: 0.7 } : {}),
          },
        })}
        rowClassName={(record) =>
          showDeleted && record.deletedAt ? 'deleted-row' : ''
        }
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <Empty
              description={effectiveEmptyMessage}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        size="middle"
      />

      {/* Detail Drawer (legacy - used when enableRecordDrawer is false) */}
      {showDetailDrawer && !enableRecordDrawer && (
        <MetadataTableDrawer
          model={model}
          record={selectedRecord}
          fields={fields}
          formSections={formSections}
          open={drawerOpen}
          onClose={closeDrawer}
        />
      )}

      {/* Record Drawer (new unified create/edit/view drawer) */}
      {enableRecordDrawer && (
        <MetadataRecordDrawer
          open={recordDrawerState.open}
          onClose={closeRecordDrawer}
          endpoint={endpoint}
          mode={recordDrawerState.mode}
          record={recordDrawerState.record}
          fields={fields}
          formSections={formSections}
          display={display}
          lifecycle={lifecycle}
          api={api}
          comparison={comparisonEnabled ? comparison : undefined}
          onCompare={comparisonEnabled ? handleCompare : undefined}
          onSuccess={handleRecordSaved}
          onOpenEditor={handleLifecycleOpenEditor}
          queryKey={queryKey}
          keyField={rowKey}
          internalState={
            recordDrawerState.duplicatedFromRecord
              ? {
                  isDirty: false,
                  duplicatedFromRecord: recordDrawerState.duplicatedFromRecord,
                  initialValues: recordDrawerState.initialValues,
                }
              : undefined
          }
        />
      )}

      {/* Column Customizer */}
      <MetadataTableColumnCustomizer
        model={model}
        availableColumns={columnInfo}
        visibleColumns={visibleColumns}
        preset={preset}
        onColumnsChange={setVisibleColumns}
        open={customizerOpen}
        onClose={() => setCustomizerOpen(false)}
        actionsVisible={actionsVisible}
        onActionsVisibleChange={setActionsVisible}
      />

      {/* Advanced Filters Drawer */}
      <AdvancedFiltersDrawer {...advancedFiltersDrawerProps} />

      {/* Bulk Operation Progress Modal */}
      <BulkOperationProgress
        visible={bulkProgress.isRunning}
        operation={bulkProgress.operation}
        total={bulkProgress.total}
        completed={bulkProgress.completed}
        failed={bulkProgress.failed}
        onCancel={cancelBulkAction}
      />

      {/* Bulk Lifecycle Operation Results Modal */}
      <BulkOperationResults
        visible={bulkResults.visible}
        operation={bulkResults.operation}
        total={bulkResults.total}
        succeeded={bulkResults.succeeded}
        failed={bulkResults.failed}
        failedItems={bulkResults.failedItems}
        fromServerMeta={bulkResults.fromServerMeta}
        previousState={bulkResults.previousState}
        newState={bulkResults.newState}
        supersededCount={bulkResults.supersededCount}
        continuationsCreated={bulkResults.continuationsCreated}
        onClose={() => {
          closeBulkResults()
          // Clear selection after viewing results
          setSelectedRowKeys([])
        }}
      />

      {/* Bulk Record Operation Progress Modal */}
      <BulkOperationProgress
        visible={bulkRecordProgress.isRunning}
        operation={bulkRecordProgress.operation}
        total={bulkRecordProgress.total}
        completed={bulkRecordProgress.completed}
        failed={bulkRecordProgress.failed}
        onCancel={cancelBulkRecordAction}
      />

      {/* Bulk Record Operation Results Modal */}
      <BulkOperationResults
        visible={bulkRecordResults.visible}
        operation={bulkRecordResults.operation}
        total={bulkRecordResults.total}
        succeeded={bulkRecordResults.succeeded}
        failed={bulkRecordResults.failed}
        failedItems={bulkRecordResults.failedItems}
        onClose={() => {
          closeBulkRecordResults()
          // Clear selection after viewing results
          setSelectedRowKeys([])
        }}
      />

      {/* Comparison Modal */}
      {comparisonEnabled && (
        <ComparisonModal
          open={comparisonModalOpen}
          onClose={handleCloseComparison}
          endpoint={endpoint}
          anchorRecord={comparisonAnchorRecord}
          comparison={comparison!}
          display={display}
          fields={fields}
          formSections={formSections}
        />
      )}

      {/* Unified Activation Modal (for bulk activate with supersession preview) */}
      <UnifiedActivationModal
        visible={showUnifiedActivationModal}
        previewData={activationPreviewData}
        isLoading={isLoadingActivationPreview}
        progress={activationPreviewProgress}
        entityLabel={display?.label?.toLowerCase() || 'records'}
        entityLabelSingular={display?.label_singular || 'Record'}
        fieldMetadata={fields}
        comparison={comparison}
        display={display}
        endpoint={endpoint}
        onToggleSelection={toggleActivationSelection}
        onSelectAll={selectAllSupersessions}
        onDeselectAll={deselectAllSupersessions}
        getActivationUids={getActivationUids}
        getActivationCount={getActivationCount}
        onConfirm={handleUnifiedActivationConfirm}
        onCancel={handleUnifiedActivationCancel}
      />
    </div>
  )
}

export default MetadataTable
